import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ArchiveTenantModal from "../../components/ArchiveTenantModal";

import { graphqlEndpoint } from "../../config";
import { READ_ALL_TENANTS } from "../../graph/tenants/queries";
import { ARCHIVE_TENANT } from "../../graph/tenants/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const ManageTenants = () => {
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [view, setView] = useState("active");
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await client.request(READ_ALL_TENANTS);
      setTenants((data.readAllTenants || []).map((tenant) => ({ ...tenant, id: tenant.id })));
    } catch (err) {
      console.error("Failed to fetch tenants:", err);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleViewChange = (_, nextView) => {
    if (!nextView) return;
    setView(nextView);
  };

  const handleOpenArchiveModal = (tenant) => {
    setSelectedTenant(tenant);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedTenant(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedTenant) return;

    try {
      await client.request(ARCHIVE_TENANT, { id: selectedTenant.id });
      handleCloseArchiveModal();
      fetchTenants();
    } catch (err) {
      console.error("Failed to archive tenant:", err);
    }
  };

  const handleEdit = (id) => {
    navigate(`/tenants/edit/${id}`);
  };

  const filteredTenants = useMemo(() => {
    return tenants.filter((tenant) => {
      const isArchived = !!tenant.archivedAt;
      return view === "archived" ? isArchived : !isArchived;
    });
  }, [tenants, view]);

  const columns = [
    { field: "name", headerName: "Name", flex: 1.2 },
    { field: "status", headerName: "Status", flex: 1 },
    { field: "slug", headerName: "Slug", flex: 1 },
    {
      field: "archivedAt",
      headerName: "Archived",
      flex: 1,
      valueGetter: (params) => (params ? new Date(params).toLocaleString() : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => handleEdit(row.id)}
          onDelete={() => handleOpenArchiveModal(row)}
          hideCreate
          hideDelete={!!row.archivedAt}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="MANAGE TENANTS" subtitle="System tenants" />

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ThemedDataGrid rows={filteredTenants} columns={columns} />

      <ArchiveTenantModal
        open={archiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        tenant={selectedTenant}
      />
    </Box>
  );
};

export default ManageTenants;